import { useState } from "react";
import { ChevronDown, ChevronUp, SendHorizontal } from "lucide-react";
import { Radio, RadioGroup } from "react-aria-components";
import { Button } from "ui-kit-v2/src/button/button";
import { Checkbox } from "ui-kit-v2/src/check-box/check-box";
import { _, Key } from "i18n";
import styles from "./styles.module.scss";
import { AICoachReviewsMode } from "common/src/gamePage/types";
import clsx from "clsx";
import { CoachReviewCoachSelect } from "../CoachSelect";
import { useActiveCoachReviewsContext } from "common/src/gamePage/hooks/useActiveCoachReviewsContext";
import { useSendPlayerReview } from "common/src/gamePage/hooks/useSendPlayerReview";
import { useSendTeamReview } from "common/src/gamePage/hooks/useSendTeamReview";
import { getDraftKey } from "common/src/gamePage/stores/coach-reviews.store";
import { useCoachReviewsStore } from "common/src/gamePage/stores/coach-reviews.store";
import { useGetReviews } from "common/src/gamePage/hooks/useGetReviews";
import { useAttachedEvents } from "common/src/gamePage/hooks/useAttachedEvents";

interface RecipientsOption {
	value: "player" | "playerAndParent";
	keys: Record<AICoachReviewsMode.Team | AICoachReviewsMode.Player, { title: Key; description: Key }>;
}

const RECIPIENT_OPTIONS: RecipientsOption[] = [
	{
		value: "player",
		keys: {
			[AICoachReviewsMode.Team]: {
				title: "game.coachReviews.sendButton.team.recipients.player.title",
				description: "game.coachReviews.sendButton.team.recipients.player.description",
			},
			[AICoachReviewsMode.Player]: {
				title: "game.coachReviews.sendButton.player.recipients.player.title",
				description: "game.coachReviews.sendButton.player.recipients.player.description",
			},
		},
	},
	{
		value: "playerAndParent",
		keys: {
			[AICoachReviewsMode.Team]: {
				title: "game.coachReviews.sendButton.team.recipients.playerAndParent.title",
				description: "game.coachReviews.sendButton.team.recipients.playerAndParent.description",
			},
			[AICoachReviewsMode.Player]: {
				title: "game.coachReviews.sendButton.player.recipients.playerAndParent.title",
				description: "game.coachReviews.sendButton.player.recipients.playerAndParent.description",
			},
		},
	},
];

type CoachReviewTarget =
	| {
			mode: AICoachReviewsMode.Team;
			numberOfPlayers: number;
	  }
	| {
			mode: AICoachReviewsMode.Player;
			playerName: string;
			playerId: number;
	  };

type CoachReviewsSendButtonProps = CoachReviewTarget & {
	hasParents: boolean;
	disabled?: boolean;
	wasSent?: boolean;
};

export const CoachReviewsSendButton = (props: CoachReviewsSendButtonProps) => {
	const { gameId, gameTeamId, userId } = useActiveCoachReviewsContext();
	const { data, isLoading, isError } = useGetReviews();
	const draftKey =
		props.mode === AICoachReviewsMode.Team
			? getDraftKey({ kind: "team", userId: userId, gameId, teamId: gameTeamId })
			: getDraftKey({ kind: "player", userId: userId, gameId, playerId: props.playerId });
	const draft = useCoachReviewsStore((state) => state.drafts[draftKey]);
	const attachedEvents = useAttachedEvents(draft?.attachedEventIds);
	const attachedEventIds = attachedEvents.flatMap((mergedEvent) => mergedEvent.eventIds);
	const sendPlayerMutation = useSendPlayerReview();
	const sendTeamMutation = useSendTeamReview();
	const [isOpen, setIsOpen] = useState(false);
	const [includeCoaches, setIncludeCoaches] = useState(false);
	const [selectedCoachIds, setSelectedCoachIds] = useState<number[]>([]);
	const [recipients, setRecipients] = useState<RecipientsOption["value"]>("player");

	if (isLoading || isError) return null;

	const { coaches } = data.data;

	const isSending = sendPlayerMutation.isLoading || sendTeamMutation.isLoading;
	const isDisabled = isSending || props.wasSent || props.disabled;

	const getButtonLabel = () => {
		if (isSending) {
			return _("game.coachReviews.sendButton.label.sending");
		}

		if (props.wasSent) {
			return _("game.coachReviews.sendButton.label.sent");
		}

		return _("game.coachReviews.sendButton.label");
	};

	const getOptionTitle = (option: RecipientsOption) => _(option.keys[props.mode].title);

	const getOptionDescription = (option: RecipientsOption) =>
		_(option.keys[props.mode].description, {
			numberOfPlayers: props.mode === AICoachReviewsMode.Team ? props.numberOfPlayers : undefined,
			playerName: props.mode === AICoachReviewsMode.Player ? props.playerName : undefined,
		});

	const handleSendToLockerRoom = () => {
		if (props.mode === AICoachReviewsMode.Player) {
			sendPlayerMutation.mutate({
				id: props.playerId,
				body: {
					text: draft?.reviewText || "",
					includeParent: recipients === "playerAndParent",
					events: attachedEventIds.length > 0 ? attachedEventIds : null,
					coaches: selectedCoachIds.length > 0 ? selectedCoachIds : null,
					badge: draft?.kind === "player" ? draft.assignedBadgeId : null,
					aiGeneratedContentId: draft?.contentId,
				},
			});
		}

		if (props.mode === AICoachReviewsMode.Team) {
			sendTeamMutation.mutate({
				id: gameTeamId,
				body: {
					text: draft?.reviewText || "",
					includeParent: recipients === "playerAndParent",
					events: attachedEventIds.length > 0 ? attachedEventIds : null,
					coaches: selectedCoachIds.length > 0 ? selectedCoachIds : null,
					aiGeneratedContentId: draft?.contentId,
				},
			});
		}

		setIsOpen(false);
	};

	return (
		<div className={styles["send"]}>
			<Button
				type="button"
				size="small"
				iconLeft={<SendHorizontal size={16} />}
				iconRight={isSending ? null : isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
				onClick={() => setIsOpen((open) => !open)}
				disabled={isDisabled}
			>
				{getButtonLabel()}
			</Button>

			{isOpen && (
				<div className={styles["send__panel"]}>
					<RadioGroup
						value={recipients}
						onChange={(value) => setRecipients(value as RecipientsOption["value"])}
						className={styles["send__options"]}
					>
						{RECIPIENT_OPTIONS.map((option) => (
							<Radio
								key={option.value}
								value={option.value}
								isDisabled={option.value === "playerAndParent" && !props.hasParents}
								className={({ isSelected, isDisabled }) =>
									clsx(
										styles["send__option"],
										isSelected && styles["send__option--selected"],
										isDisabled && styles["send__option--disabled"]
									)
								}
							>
								<span className={styles["send__option-indicator"]} />
								<span className={styles["send__option-text"]}>
									<span className={styles["send__option-title"]}>{getOptionTitle(option)}</span>
									<span className={styles["send__option-description"]}>{getOptionDescription(option)}</span>
								</span>
							</Radio>
						))}
					</RadioGroup>
					{coaches.length > 0 && (
						<div className={styles["send__coaches"]}>
							<Checkbox
								label={_("game.coachReviews.sendButton.includeCoaches.label")}
								checked={includeCoaches}
								disabled={coaches.length === 0}
								onChange={(event) => {
									// Reset the ids when unchecking the include coaches option
									if (!event.target.checked) {
										setSelectedCoachIds([]);
									}
									setIncludeCoaches(event.target.checked);
								}}
							/>
							{includeCoaches && (
								<CoachReviewCoachSelect coaches={coaches} selectedIds={selectedCoachIds} onChange={setSelectedCoachIds} />
							)}
						</div>
					)}
					<div className={styles["send__actions"]}>
						<Button
							type="button"
							size="small"
							variant="secondary"
							iconLeft={<SendHorizontal size={16} />}
							onClick={handleSendToLockerRoom}
						>
							{_("game.coachReviews.sendButton.label.send")}
						</Button>
						<Button type="button" size="small" variant="ghost" onClick={() => setIsOpen(false)}>
							{_("game.coachReviews.sendButton.label.cancel")}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
